import React, { useEffect, useState } from "react";
import { api, fmtMoney } from "@/lib/api";
import { Shield, Users, FileText, Receipt, Hammer } from "lucide-react";
import { toast } from "sonner";

export default function Admin() {
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);

  const load = () => {
    api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const toggle = async (u) => {
    try {
      await api.patch(`/admin/users/${u.id}/active`, { is_active: !u.is_active });
      toast.success(`${u.email} ${u.is_active ? "disabled" : "enabled"}`);
      load();
    } catch {
      toast.error("Could not update user");
    }
  };

  return (
    <div className="stagger-in">
      <div className="flex items-center gap-2">
        <Shield size={14} className="text-[var(--secondary)]" />
        <div className="label-mono">/ ADMIN</div>
      </div>
      <h1 className="font-display text-5xl tracking-tight mt-2">
        Control <span className="text-[var(--secondary)]">Room</span>
      </h1>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-10">
        <Stat icon={Users} label="Users" value={stats?.users ?? "—"} />
        <Stat icon={Users} label="Active" value={stats?.active_users ?? "—"} accent />
        <Stat icon={Users} label="Clients" value={stats?.clients ?? "—"} />
        <Stat icon={Hammer} label="Quotes" value={stats?.jobs ?? "—"} />
        <Stat icon={Receipt} label="Invoices" value={stats?.invoices ?? "—"} />
      </div>

      <div className="mt-10">
        <div className="label-mono mb-4">/ USERS</div>
        <div className="border border-[var(--border)] overflow-x-auto" data-testid="admin-users-list">
          <div className="min-w-[820px]">
          <div className="grid grid-cols-12 gap-0 text-xs font-mono uppercase tracking-wider text-zinc-500 border-b border-[var(--border)]">
            <div className="col-span-3 p-3">Email</div>
            <div className="col-span-2 p-3 border-l border-[var(--border)]">Name</div>
            <div className="col-span-2 p-3 border-l border-[var(--border)]">Business</div>
            <div className="col-span-1 p-3 border-l border-[var(--border)]">Role</div>
            <div className="col-span-2 p-3 border-l border-[var(--border)]">Created</div>
            <div className="col-span-2 p-3 border-l border-[var(--border)]">Status</div>
          </div>
          {users.map((u) => (
            <div
              key={u.id}
              className="grid grid-cols-12 gap-0 text-sm font-mono border-b border-[var(--border)] last:border-b-0"
              data-testid={`admin-user-${u.id}`}
            >
              <div className="col-span-3 p-3 truncate">{u.email}</div>
              <div className="col-span-2 p-3 border-l border-[var(--border)] truncate text-zinc-400">
                {u.name}
              </div>
              <div className="col-span-2 p-3 border-l border-[var(--border)] truncate text-zinc-400">
                {u.business_name || "—"}
              </div>
              <div className="col-span-1 p-3 border-l border-[var(--border)] text-[var(--primary)] uppercase text-xs">
                {u.role}
              </div>
              <div className="col-span-2 p-3 border-l border-[var(--border)] text-zinc-400">
                {new Date(u.created_at).toLocaleDateString("en-NZ")}
              </div>
              <div className="col-span-2 p-3 border-l border-[var(--border)]">
                <button
                  onClick={() => toggle(u)}
                  disabled={u.role === "admin"}
                  className={`text-xs uppercase tracking-wider border px-3 py-1 ${
                    u.is_active
                      ? "text-[var(--primary)] border-[var(--primary)]"
                      : "text-[var(--error)] border-[var(--error)]"
                  } disabled:opacity-40`}
                  data-testid={`admin-toggle-${u.id}`}
                >
                  {u.is_active ? "Active" : "Disabled"}
                </button>
              </div>
            </div>
          ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="card-sharp p-5">
      <Icon size={16} className="text-zinc-500 mb-3" />
      <div className="label-mono">{label}</div>
      <div
        className={`font-mono text-3xl mt-1 ${accent ? "text-[var(--primary)]" : "text-white"}`}
      >
        {typeof value === "number" ? value : value}
      </div>
    </div>
  );
}
